import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  Plug,
  Users,
  UsersRound,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTopFriends } from '../../hooks/useTopFriends';
import { useUnreadMessageCount } from '../../hooks/useUnreadMessageCount';
import { useStashScopedStore } from '../../store/stash-scoped/context';

const BadgeCast = Badge as React.ComponentType<{
  variant?: string;
  className?: string;
  children?: React.ReactNode;
}>;

const NAV_ITEMS = [
  { label: 'Ask STASH', to: '/app/chat', icon: MessageSquare },
  { label: 'Friends', to: '/app/friends', icon: Users },
  { label: 'STASH Board', to: '/app/stashboard', icon: LayoutDashboard },
  { label: 'Groups', to: '/app/groups', icon: UsersRound },
  { label: 'Messages', to: '/app/messages', icon: MessageCircle },
  { label: 'Connected Apps', to: '/app/connected-apps', icon: Plug },
] as const;

const activeClass =
  'flex items-center gap-3 h-10 rounded-xl px-3 text-sm font-semibold bg-[var(--stash-blue-light)] text-[var(--stash-blue-dark)]';
const inactiveClass =
  'flex items-center gap-3 h-10 rounded-xl px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

function initials(name: string): string {
  return name
    .split(' ')
    .map(s => s[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function AppSidebar({ className }: { className?: string }): React.JSX.Element {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const friends = useTopFriends();
  const unreadCount = useUnreadMessageCount();

  const currentUserStasherId = useStashScopedStore(s => s.currentUserStasherId);
  const selectedHomeBase = useStashScopedStore(s =>
    s.stashers.find(st => st.recordID === currentUserStasherId)?.selectedHomeBase ?? null,
  );

  const userName = currentUser?.name ?? '';

  return (
    <nav
      aria-label="Primary"
      className={`w-60 shrink-0 border-r border-border bg-card flex flex-col${className ? ` ${className}` : ''}`}
    >
      {/* Brand block */}
      <div className="pt-[22px] px-3 pb-2">
        <button
          type="button"
          onClick={() => navigate('/app/stashboard')}
          className="text-xl font-bold uppercase tracking-wide text-foreground hover:opacity-80 transition-opacity"
        >
          STASH<span className="text-[var(--stash-blue)]">.</span>
        </button>
      </div>

      {/* Primary nav items */}
      <div role="list" className="flex flex-col gap-0.5 px-2 py-2">
        {NAV_ITEMS.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => (isActive ? activeClass : inactiveClass)}
          >
            <Icon size={16} />
            <span>{label}</span>
            {label === 'Messages' && unreadCount > 0 && (
              <BadgeCast variant="default" className="ml-auto h-5 min-w-5 rounded-full px-1.5 text-xs">
                {unreadCount}
              </BadgeCast>
            )}
          </NavLink>
        ))}
      </div>

      {/* HomeBase block */}
      {selectedHomeBase && (
        <div className="mx-2 mt-2">
          <button
            type="button"
            onClick={() => console.log('HomeBase picker')}
            className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm hover:bg-muted transition-colors"
          >
            <span className="truncate font-medium text-foreground">{selectedHomeBase.name}</span>
            <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Friends block */}
      {friends.length > 0 && (
        <div className="mx-2 mt-4 px-1">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Friends
          </p>
          <div className="flex flex-col gap-1">
            {friends.slice(0, 3).map(friend => {
              const name = `${friend.firstName} ${friend.lastName}`;
              const photoUrl = friend.profilePhotoUrl;
              return (
                <button
                  key={friend.recordID}
                  type="button"
                  onClick={() => navigate(`/app/stashboard?stasher=${friend.recordID}`)}
                  className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 text-left text-sm hover:bg-muted transition-colors"
                >
                  <Avatar className="h-7 w-7 shrink-0">
                    {photoUrl && <AvatarImage src={photoUrl} alt={name} />}
                    <AvatarFallback className="text-[10px]">{initials(name)}</AvatarFallback>
                  </Avatar>
                  <span className="truncate font-medium text-foreground">{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Current user chip */}
      <div className="mt-auto px-2 pb-2">
        <div className="flex items-center gap-2.5 rounded-xl bg-secondary p-2.5">
          <Avatar className="h-8 w-8 shrink-0">
            {currentUser?.avatar && <AvatarImage src={currentUser.avatar} alt={userName} />}
            <AvatarFallback className="text-xs">{initials(userName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{userName}</p>
            {selectedHomeBase && (
              <p className="truncate text-xs text-muted-foreground">{selectedHomeBase.name}</p>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
